export type IntegrationCatalogCategory =
  | "crm"
  | "commerce"
  | "sales"
  | "social"
  | "automation"
  | "accounting";

export type IntegrationCatalogAvailability = "available" | "coming_soon";
export type IntegrationConnectRequestBody = "provider" | "empty" | "none";
export type IntegrationConnectAuthUrlPath = "authUrl" | "data.authUrl";

export interface IntegrationConnectConfig {
  path: string;
  body?: IntegrationConnectRequestBody;
  providerId?: string;
  authUrlPath?: IntegrationConnectAuthUrlPath;
}

export interface IntegrationCatalogEntry {
  id: string;
  providerId: string;
  name: string;
  description: string;
  category: IntegrationCatalogCategory;
  availability: IntegrationCatalogAvailability;
  requiresShopDomain?: boolean;
  connect?: IntegrationConnectConfig;
}

export const INTEGRATION_CATALOG: readonly IntegrationCatalogEntry[] = [
  {
    id: "ghl",
    providerId: "ghl",
    name: "HighLevel",
    description: "Sync locations, contacts, and opportunities through the mounted OAuth seam.",
    category: "crm",
    availability: "available",
  },
  {
    id: "hubspot",
    providerId: "hubspot",
    name: "HubSpot",
    description: "Connect HubSpot CRM records through the canonical control-plane OAuth flow.",
    category: "crm",
    availability: "available",
  },
  {
    id: "salesforce",
    providerId: "salesforce",
    name: "Salesforce",
    description: "Connect Salesforce accounts with the mounted generic OAuth callback runtime.",
    category: "sales",
    availability: "available",
  },
  {
    id: "zoho",
    providerId: "zoho",
    name: "Zoho CRM",
    description: "Connect Zoho CRM through the mounted control-plane integrations seam.",
    category: "crm",
    availability: "available",
  },
  {
    id: "meta",
    providerId: "meta",
    name: "Facebook Pages (Meta)",
    description: "Connect Facebook Pages through the mounted scoped Meta OAuth seam for page discovery, reviews, and status.",
    category: "social",
    availability: "available",
  },
  {
    id: "instagram",
    providerId: "meta",
    name: "Instagram Business",
    description: "Connect Instagram Business assets through the same scoped Meta OAuth app connection used by Pages.",
    category: "social",
    availability: "available",
  },
  {
    id: "tiktok",
    providerId: "tiktok",
    name: "TikTok",
    description: "Connect TikTok accounts through the mounted TikTok OAuth route with publisher scopes.",
    category: "social",
    availability: "available",
    connect: {
      path: "/api/integrations/oauth/tiktok/initiate",
      body: "none",
    },
  },
  {
    id: "linkedin",
    providerId: "linkedin",
    name: "LinkedIn",
    description: "Connect LinkedIn member or organization accounts through the mounted scoped OAuth route.",
    category: "social",
    availability: "available",
    connect: {
      path: "/api/integrations/oauth/linkedin/initiate",
      body: "none",
    },
  },
  {
    id: "youtube",
    providerId: "youtube",
    name: "YouTube",
    description: "Connect YouTube channels through the mounted scoped Google OAuth route for upload and status.",
    category: "social",
    availability: "available",
    connect: {
      path: "/api/integrations/oauth/youtube/initiate",
      body: "none",
    },
  },
  {
    id: "google-business-profile",
    providerId: "google_business_profile",
    name: "Google Business Profile",
    description: "Connect Google Business Profile through the mounted GBP OAuth seam for review status and sync.",
    category: "social",
    availability: "available",
    connect: {
      path: "/crm/reviews/gbp/connect",
      body: "empty",
      authUrlPath: "data.authUrl",
    },
  },
  {
    id: "shopify",
    providerId: "shopify",
    name: "Shopify",
    description: "Authorize a Shopify store and reuse the same credentials for ecommerce sync.",
    category: "commerce",
    availability: "available",
    requiresShopDomain: true,
  },
  {
    id: "quickbooks",
    providerId: "quickbooks",
    name: "QuickBooks",
    description: "Accounting integration is planned but not mounted on the backend yet.",
    category: "accounting",
    availability: "coming_soon",
  },
  {
    id: "zapier",
    providerId: "zapier",
    name: "Zapier",
    description: "Automation bridge is planned but there is no live connect runtime today.",
    category: "automation",
    availability: "coming_soon",
  },
] as const;
